/**
 * `InfoHint` — icona "i" con un piccolo popover contestuale.
 *
 * Pensato per mostrare testi brevi (es. la descrizione di una skill) senza
 * ricorrere a una `Modal`, che sarebbe eccessiva. Il trigger è un'icona "info"
 * discreta; il popover si apre al passaggio del mouse e al focus da tastiera
 * (accessibile), e con un click su touch. Si chiude su `Esc`, uscita del
 * mouse, blur o click all'esterno.
 *
 * Il popover è renderizzato in un **portale** su `document.body` e posizionato
 * in coordinate assolute rispetto al trigger: così non viene tagliato
 * dall'overflow di tabelle/contenitori con scroll, a differenza di un tooltip
 * puramente CSS. La posizione è ricalcolata all'apertura e su scroll/resize.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { Icon } from './Icon';

export interface InfoHintProps {
  /** Testo mostrato nel popover. Se vuoto/assente il componente non rende nulla. */
  text?: string | null;
  /**
   * Etichetta accessibile del trigger (letta dagli screen reader). Utile per
   * contestualizzare, es. `Descrizione di React`.
   */
  label?: string;
  /** Lato dell'icona in px (default 15). */
  size?: number;
  /** Classe extra sul trigger (es. per l'allineamento nella cella). */
  className?: string;
}

/** Distanza (px) tra il trigger e il popover. */
const GAP = 8;
/** Margine minimo (px) dai bordi del viewport. */
const MARGIN = 8;
/** Larghezza massima (px) del popover, coerente con il CSS. */
const MAX_WIDTH = 280;

interface Coords {
  top: number;
  left: number;
}

export function InfoHint({ text, label, size = 15, className }: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverId = useId();

  const trimmed = (text ?? '').trim();

  /** Calcola la posizione del popover a partire dal rect del trigger. */
  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (el === null) return;
    const rect = el.getBoundingClientRect();
    // Preferenza: sotto al trigger, allineato al bordo destro (l'icona è a
    // destra nella cella). Clamp orizzontale ai margini del viewport.
    const width = Math.min(MAX_WIDTH, window.innerWidth - MARGIN * 2);
    let left = rect.right - width;
    if (left < MARGIN) left = MARGIN;
    if (left + width > window.innerWidth - MARGIN) {
      left = window.innerWidth - MARGIN - width;
    }
    const top = rect.bottom + GAP;
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const onScrollOrResize = () => updatePosition();
    const onDocPointerDown = (e: PointerEvent) => {
      // Un click sul trigger è gestito dal toggle; qui chiudiamo se il click è
      // fuori dal trigger (il popover non è interattivo, non serve escluderlo).
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    // `capture` per intercettare lo scroll anche su contenitori annidati.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, updatePosition]);

  // Niente descrizione: non rendere alcun trigger.
  if (trimmed === '') return null;

  const triggerClasses = ['info-hint', className ?? ''].filter(Boolean).join(' ');

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  return (
    <span
      ref={triggerRef}
      role="button"
      tabIndex={0}
      className={triggerClasses}
      aria-label={label ?? 'Descrizione'}
      aria-expanded={open}
      aria-describedby={open ? popoverId : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen((prev) => !prev);
      }}
      onKeyDown={onTriggerKeyDown}
    >
      <Icon name="info" size={size} />
      {open &&
        coords !== null &&
        createPortal(
          <span
            id={popoverId}
            role="tooltip"
            className="info-hint__popover"
            style={{ top: coords.top, left: coords.left, maxWidth: MAX_WIDTH }}
          >
            {trimmed}
          </span>,
          document.body,
        )}
    </span>
  );
}

export default InfoHint;
